import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { FaBell } from 'react-icons/fa';
import { useNotifications } from '../hooks/useNotifications';
import { markAsRead } from '../api/notifications';
import { generateNotificationLink } from '../utils/notificationUtils';
import { Tooltip } from './Tooltip';
import { getBadgeTailwindClasses } from '../styles/designSystem';

export const NotificationButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, loading, error, refresh } = useNotifications();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification: any) => {
    console.log('🔔 Notification clicked:', notification);
    
    // Mark as read if unread
    if (!notification.is_read && keycloak.token) {
      console.log('📝 Marking notification as read...');
      try {
        await markAsRead(keycloak.token, notification.id);
        refresh();
      } catch (err) {
        console.error("Failed to mark notification as read", err);
      }
    }
    
    // Generate deep link and navigate
    const link = generateNotificationLink(notification);
    console.log('🚀 Navigating to:', link);
    
    // Close dropdown
    setIsOpen(false);
    
    // Navigate to the link
    navigate(link);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content="Notifications">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative h-9 w-9 rounded-lg border border-[#eaeaea] bg-white hover:bg-[#fafafa] hover:border-[#ddd] focus:outline-none transition-all duration-150 flex items-center justify-center"
        >
          <FaBell className="w-3.5 h-3.5 text-[#888]" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 block h-2.5 w-2.5 rounded-full bg-red-500 ring-1.5 ring-white" />
          )}
        </button>
      </Tooltip>
      
      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-80 max-h-96 overflow-y-auto bg-white border border-[#eaeaea] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] z-50" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="px-4 py-2.5 border-b border-[#eaeaea]">
            <h3 className="text-[13px] font-semibold text-[#171717]">Notifications <span className={getBadgeTailwindClasses('dev')}>dev</span></h3>
          </div>
          
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-[#aaa]">Loading...</div>
          )}
          
          {error && (
            <div className="px-4 py-6 text-center text-sm text-[#aaa]">{error}</div>
          )}
          
          {!loading && !error && notifications.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-[#aaa]">No new notifications</div>
          )}
          
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <div 
                key={notification.id} 
                className={`px-4 py-3 border-b border-[#f5f5f5] last:border-b-0 ${
                  !notification.is_read ? 'bg-[#fafafa]' : ''
                } hover:bg-[#fafafa] transition-colors cursor-pointer`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className={`w-2 h-2 rounded-full ${
                      !notification.is_read ? 'bg-blue-400' : 'bg-[#eaeaea]'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#444] leading-relaxed">
                      {notification.message}
                    </p>
                    <p className="text-xs text-[#aaa] mt-1">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 